import React, { useEffect, useState } from 'react';
import { X, Sparkles, Loader2, RefreshCw, Lightbulb, CheckCircle2, HelpCircle, AlertTriangle, ArrowRight, Circle } from 'lucide-react';
import { summarizeDecisions, type DecisionBrief, type BriefSection } from '../../services/artifactService';
import { trackDecisionPaneOpened, trackDecisionBriefGenerated } from '../../services/analyticsService';
import { TYPE } from '../../constants/typeScale';

/**
 * Decision brief panel (analyst-workflow Synthesize step) — a slide-over that distils the group chat into a
 * FLEXIBLE multi-lens view: the sections are whatever headings fit THIS conversation (not a forced four), each
 * item attributed to the specialist (lens) who raised it. On-demand + metered. Additive overlay — mirrors the
 * existing lightbox/panel pattern (fixed inset, monochrome, Lucide), does NOT change the chat layout backbone.
 */
/** Pick a fitting icon for a flexible heading (keyword match; a neutral dot otherwise). */
function iconFor(heading: string): React.FC<{ className?: string }> {
  const h = heading.toLowerCase();
  if (h.includes('idea') || h.includes('proposal') || h.includes('angle')) return Lightbulb;
  if (h.includes('decision') || h.includes('agree') || h.includes('conclusion')) return CheckCircle2;
  if (h.includes('question') || h.includes('open') || h.includes('unknown')) return HelpCircle;
  if (h.includes('risk') || h.includes('caveat') || h.includes('disagree') || h.includes('concern')) return AlertTriangle;
  if (h.includes('next') || h.includes('step') || h.includes('action')) return ArrowRight;
  return Circle;
}

const Section: React.FC<{ section: BriefSection }> = ({ section }) => {
  const Icon = iconFor(section.heading);
  return (
    <div>
      <div className={`flex items-center gap-1.5 ${TYPE.control} font-semibold uppercase tracking-wide text-secondary-500 dark:text-secondary-400 mb-1.5`}>
        <Icon className="w-3.5 h-3.5" /> {section.heading} <span className="text-secondary-300 dark:text-secondary-600">· {section.items.length}</span>
      </div>
      <ul className="space-y-1.5 mb-3">
        {section.items.map((it, i) => (
          <li key={i} className={`${TYPE.body} text-secondary-800 dark:text-secondary-200 leading-snug`}>
            {it.text}
            {it.by && <span className="text-secondary-400 dark:text-secondary-500"> — {it.by}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
};

const DecisionBriefPanel: React.FC<{ open: boolean; conversationId: string; onClose: () => void }> = ({ open, conversationId, onClose }) => {
  const [brief, setBrief] = useState<DecisionBrief | null>(null);
  const [asOf, setAsOf] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Intent funnel step — the analyst opened the Synthesize pane.
  useEffect(() => { if (open) trackDecisionPaneOpened(conversationId); }, [open, conversationId]);

  const generate = async () => {
    setLoading(true); setError('');
    try {
      const r = await summarizeDecisions(conversationId);
      setBrief(r.brief); setAsOf(r.messageCount);
      // ACTIVATION: a thesis was pressure-tested. GA4 flags the first per-user occurrence as activation.
      trackDecisionBriefGenerated(conversationId, {
        messages: r.messageCount,
        sections: r.brief.sections.length,
        items: r.brief.sections.reduce((n, s) => n + s.items.length, 0),
      });
    } catch (e: any) { setError(e?.message || 'Could not synthesize this chat'); }
    finally { setLoading(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex justify-end bg-black/40" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md h-full bg-white dark:bg-secondary-900 border-l border-secondary-200 dark:border-secondary-600 flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-secondary-200 dark:border-secondary-600 shrink-0">
          <h2 className="font-semibold text-secondary-900 dark:text-white">Decision brief</h2>
          <button onClick={onClose} aria-label="Close" className="rounded-lg p-1.5 text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-200 hover:bg-secondary-100 dark:hover:bg-secondary-800"><X className="w-4 h-4" /></button>
        </div>

        {/* Body — four states */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {!brief && !loading && !error && (
            <div className="text-center py-10">
              <Sparkles className="w-6 h-6 mx-auto text-secondary-300 dark:text-secondary-600 mb-3" />
              <p className={`${TYPE.body} text-secondary-600 dark:text-secondary-300 mb-1`}>Synthesize this chat</p>
              <p className={`${TYPE.control} text-secondary-400 mb-5 max-w-xs mx-auto`}>Distil what your specialists proposed, decided, left open, and flagged as risks — attributed to each lens.</p>
              <button onClick={generate}
                className={`inline-flex items-center gap-1.5 rounded-lg bg-secondary-900 dark:bg-white px-4 py-2 ${TYPE.sectionTitle} text-white dark:text-secondary-900 hover:bg-black dark:hover:bg-secondary-100`}>
                <Sparkles className="w-4 h-4" /> Generate
              </button>
            </div>
          )}
          {loading && <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-secondary-400" /></div>}
          {error && !loading && (
            <div className="text-center py-10">
              <p className={`${TYPE.body} text-secondary-500 mb-3`}>{error}</p>
              <button onClick={generate} className={`inline-flex items-center gap-1.5 rounded-lg border border-secondary-300 dark:border-secondary-600 px-3 py-1.5 ${TYPE.control} font-medium text-secondary-600 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-800`}><RefreshCw className="w-3.5 h-3.5" /> Try again</button>
            </div>
          )}
          {brief && !loading && (
            <>
              {brief.thesis && <p className={`mb-4 ${TYPE.body} font-medium text-secondary-900 dark:text-white leading-snug`}>{brief.thesis}</p>}
              {brief.sections.length === 0
                ? <p className={`${TYPE.control} text-secondary-400 italic py-6 text-center`}>Nothing substantive to synthesize yet.</p>
                : brief.sections.map((s, i) => <Section key={i} section={s} />)}
              <div className="mt-2 flex items-center justify-between border-t border-secondary-100 dark:border-secondary-600 pt-3">
                <span className={`${TYPE.meta} text-secondary-400`}>{asOf != null ? `as of ${asOf} message${asOf === 1 ? '' : 's'}` : ''}</span>
                <button onClick={generate} className={`inline-flex items-center gap-1.5 ${TYPE.control} text-secondary-500 hover:text-secondary-800 dark:hover:text-secondary-200`}><RefreshCw className="w-3.5 h-3.5" /> Regenerate</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DecisionBriefPanel;
